import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import styles from './LassoSpinner.scss?inline';

const RING_RADIUS = 42;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const SHOW_DELAY_MS = 150; // Espera antes de mostrarse: evita el parpadeo en taps rápidos que se sueltan antes de este punto

/**
 * Indicador visual de "mantener presionado" (long-press) que precede a la
 * activación del lasso automático en el campo de burbujas.
 */
@customElement('app-lasso-spinner')
export class LassoSpinner extends LitElement {
    static styles = [unsafeCSS(styles)];

    @property({ type: Number, attribute: 'duration-ms' }) durationMs = 1000;

    @query('.glow') private _glow!: HTMLElement;
    @query('.ring-progress') private _ringProgress!: SVGCircleElement;

    private _showTimeoutId: ReturnType<typeof setTimeout> | null = null;

    firstUpdated() {
        // 'animationend' solo se dispara si la animación llega a su fin de forma natural;
        // si se cancela (hide()), el navegador emite 'animationcancel' en su lugar.
        this._ringProgress.addEventListener('animationend', this._handleSweepEnd);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._ringProgress?.removeEventListener('animationend', this._handleSweepEnd);
        this._cancelPendingShow();
    }

    render() {
        return html`
            <div class="glow"></div>
            <svg class="ring" viewBox="0 0 100 100">
                <defs>
                    <linearGradient id="lasso-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stop-color="rgba(129, 199, 255, 0.25)"></stop>
                        <stop offset="55%" stop-color="rgba(66, 134, 244, 0.95)"></stop>
                        <stop offset="100%" stop-color="rgba(157, 92, 246, 0.95)"></stop>
                    </linearGradient>
                </defs>
                <circle class="ring-track" cx="50" cy="50" r="${RING_RADIUS}"></circle>
                <circle class="ring-progress" cx="50" cy="50" r="${RING_RADIUS}"
                    stroke="url(#lasso-gradient)"
                    stroke-dasharray="${RING_CIRCUMFERENCE}"
                    stroke-dashoffset="${RING_CIRCUMFERENCE}"
                    style="--spinner-circumference: ${RING_CIRCUMFERENCE}"></circle>
            </svg>
        `;
    }

    /** Posiciona el spinner y (re)inicia, tras un breve delay, su animación de progreso. */
    show(x: number, y: number) {
        this.style.left = `${x}px`;
        this.style.top = `${y}px`;
        this.style.setProperty('--spinner-duration', `${this.durationMs}ms`);

        this._cancelPendingShow();
        this._showTimeoutId = setTimeout(() => {
            this._showTimeoutId = null;
            this.classList.add('visible');
            this._restartAnimation();
        }, SHOW_DELAY_MS);
    }

    /** Detiene la animación en curso y oculta el elemento (o cancela un show() pendiente). */
    hide() {
        this._cancelPendingShow();
        this.classList.remove('visible');
        this._glow?.classList.remove('animate');
        this._ringProgress?.classList.remove('animate');
    }

    private _cancelPendingShow() {
        if (this._showTimeoutId !== null) {
            clearTimeout(this._showTimeoutId);
            this._showTimeoutId = null;
        }
    }

    private _restartAnimation() {
        const glow = this._glow;
        const ring = this._ringProgress;
        if (!glow || !ring) return;

        glow.classList.remove('animate', 'complete');
        ring.classList.remove('animate', 'complete');
        // Forzar reflow para poder reiniciar la animación CSS al mostrarse de nuevo
        void ring.getBoundingClientRect();
        glow.classList.add('animate');
        ring.classList.add('animate');
    }

    /** Se ejecuta solo cuando el barrido del anillo completa su recorrido sin ser interrumpido. */
    private _handleSweepEnd = (ev: AnimationEvent) => {
        if (ev.animationName !== 'lasso-ring-sweep') return;

        const glow = this._glow;
        const ring = this._ringProgress;
        if (glow && ring) {
            glow.classList.remove('animate');
            ring.classList.remove('animate');
            void ring.getBoundingClientRect();
            glow.classList.add('complete');
            ring.classList.add('complete');
        }

        this.dispatchEvent(new CustomEvent('lasso-complete', { bubbles: true, composed: true }));
    };
}

declare global {
    interface HTMLElementTagNameMap {
        'app-lasso-spinner': LassoSpinner;
    }
}
